import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { workspaceApi } from '@/api/workspaceApi'
import { usePaginatedList } from '@/hooks/usePaginatedList'
import { SiteShell } from '@/components/layout/SiteShell'
import { EmptyState } from '@/components/ui/EmptyState'
import { SkeletonCard, SkeletonInput } from '@/components/ui/Skeleton'
import { Pagination } from '@/components/ui/Pagination'
import { AvatarStack, avatarColor } from '@/components/ui/AvatarStack'
import { PlanChip } from '@/components/billing/PlanChip'
import type { Workspace, WorkspaceMember } from '@/api/types'
import type { WorkspaceListParams } from '@/api/workspaceApi'


const DEFAULT_FILTERS: WorkspaceListParams = {
  expand: 'members.user,subscription_summary',
}

function workspaceMembersToAvatarItems(members: WorkspaceMember[]) {
  return members.map((m) => ({
    id: m.user.id,
    name: m.user.name || m.user.email,
    email: m.user.email,
    color: avatarColor(m.user.id),
  }))
}

function WorkspaceCard({
  ws,
  onOpen,
}: {
  ws: Workspace
  onOpen: (slug: string) => void
}) {
  return (
    <button
      onClick={() => onOpen(ws.slug)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'left',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '16px 20px',
        cursor: 'pointer',
        color: 'var(--text)',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{ws.name}</span>
          <span style={{ fontSize: 11, opacity: 0.4, fontFamily: 'monospace' }}>{ws.slug}</span>
        </div>
        <PlanChip code={ws.subscription_summary?.plan_code ?? 'free'} />
      </div>
      {(ws.members ?? []).length > 0 && (
        <AvatarStack
          items={workspaceMembersToAvatarItems(ws.members ?? [])}
          max={5}
          size={24}
          totalCount={ws.member_count}
        />
      )}
    </button>
  )
}

export default function WorkspacesListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const memberships = useAuthStore((s) => s.memberships)
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace)
  const workspaceListVersion = useWorkspaceStore((s) => s.workspaceListVersion)

  const { items, total, loading, page, totalPages, filters, setFilter, setPage, reload } =
    usePaginatedList<Workspace, WorkspaceListParams>(
      (params) => workspaceApi.list(params),
      {
        initialLimit: 12,
        initialFilters: DEFAULT_FILTERS,
        enabled: memberships.length > 0,
      },
    )

  useEffect(() => {
    if (workspaceListVersion > 0) reload()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceListVersion])

  async function open(slug: string) {
    await selectWorkspace(slug)
    navigate(`/workspace/${slug}/projects`)
  }

  const searchValue = (filters.name as string) ?? ''

  return (
    <SiteShell>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>{t('workspaces.title', 'Workspaces')}</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.55 }}>
              {t('workspaces.subtitle', 'Selecione um workspace para abrir')}
            </p>
          </div>
          <button onClick={() => navigate('/workspace/create')} style={primaryBtn}>
            {t('workspaces.create', '+ Novo workspace')}
          </button>
        </header>

        <div style={{ padding: '24px 32px' }}>
          <div style={{ marginBottom: 16 }}>
            {loading && total === 0 ? (
              <SkeletonInput maxWidth={320} />
            ) : (
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setFilter('name', e.target.value)}
                placeholder={t('workspaces.search', 'Buscar workspace...')}
                className="morph-input"
                style={{ maxWidth: 320 }}
              />
            )}
          </div>

          {loading ? (
            <div style={gridStyle}>
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} height={96} />
              ))}
            </div>
          ) : total === 0 && !searchValue ? (
            <EmptyState
              icon="📂"
              title={t('workspaces.empty.title', 'Você ainda não tem workspaces')}
              description={t('workspaces.empty.subtitle', 'Crie um para começar.')}
              actions={
                <button onClick={() => navigate('/workspace/create')} style={primaryBtn}>
                  {t('workspaces.create', '+ Novo workspace')}
                </button>
              }
            />
          ) : items.length === 0 ? (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--text-dim)',
              }}
            >
              {t('workspaces.noMatch', 'Nenhum workspace corresponde à busca.')}
            </div>
          ) : (
            <>
              <div style={gridStyle}>
                {items.map((ws) => (
                  <WorkspaceCard
                    key={ws.id}
                    ws={ws}
                    onOpen={(slug) => void open(slug)}
                  />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </SiteShell>
  )
}

const containerStyle: React.CSSProperties = {
  flex: 1,
  background: 'var(--bg-app)',
  color: 'var(--text)',
  fontFamily: 'system-ui, sans-serif',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 32px 16px',
  borderBottom: '1px solid var(--border)',
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 14,
}

const primaryBtn: React.CSSProperties = {
  background: '#3b82f6',
  border: 'none',
  color: 'white',
  borderRadius: 8,
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
}
